import { useState, useEffect, ReactNode } from 'react';
import { TOP_NAV, TOP_NAV_LABELS, STAGES } from '../config';
import { useApi, api } from '../api/client';
import AIAssistant from './AIAssistant';
import { AIPanelProvider } from './AIPanel';

// Theme hook with localStorage persistence
function useTheme() {
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    return (localStorage.getItem('theme') as 'dark' | 'light') || 'dark';
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);
  const toggle = () => setTheme(t => t === 'dark' ? 'light' : 'dark');
  return { theme, toggle };
}

interface Props {
  currentView: string;
  currentModule: string;
  onNavigate: (view: string) => void;
  onOpenModule: (module: string) => void;
  children: ReactNode;
}

export default function Layout({ currentView, currentModule, onNavigate, onOpenModule, children }: Props) {
  const [openStages, setOpenStages] = useState<Set<number>>(new Set([1, 3]));
  const { theme, toggle } = useTheme();

  const toggleStage = (id: number) => {
    setOpenStages(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <AIPanelProvider>
      <a href="#main-content" className="skip-link">跳到主要内容</a>
      <Topbar currentView={currentView} onNavigate={onNavigate} theme={theme} onToggleTheme={toggle} />
      <TechBar />
      <div className="layout">
        <Sidebar
          currentModule={currentModule}
          openStages={openStages}
          toggleStage={toggleStage}
          onOpenModule={onOpenModule}
        />
        <main className="main" id="main-content" role="main" tabIndex={-1}>
          {children}
        </main>
        <QuickPanel />
      </div>
      <AIAssistant />
    </AIPanelProvider>
  );
}

// ===== Shared Clock Hook =====
function useClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

// ===== LiveClock =====
function LiveClock() {
  const now = useClock();
  const t = now.toLocaleTimeString('en-GB', { hour12: false });
  return (
    <span className="live-tag">
      <span className="dot" />
      <span className="st">ONLINE</span>
      <span className="clk">{t}</span>
    </span>
  );
}

// ===== TechBar (terminal status strip) =====
function TechBar() {
  const now = useClock();
  const t = now.toLocaleTimeString('en-GB', { hour12: false });
  const d = now.toISOString().slice(0, 10).replace(/-/g, '.');
  return (
    <div className="tech-bar">
      <span className="tech-bar-item"><span className="tech-bar-dot" /><span className="tb-val">SYS::LIVE</span></span>
      <span className="tech-bar-item"><span className="tb-key">STATUS</span><span className="tb-ok">ONLINE</span></span>
      <span className="tech-bar-item"><span className="tb-key">CLK</span><span className="tb-val">{t}</span></span>
      <span className="tech-bar-item"><span className="tb-key">DATE</span><span className="tb-val">{d}</span></span>
      <span className="tech-bar-item"><span className="tb-key">VER</span><span className="tb-val">v0.3.7</span></span>
      <span className="tech-bar-item"><span className="tb-key">NODE</span><span className="tb-val">CF-PAGES</span></span>
      <span className="tech-bar-item"><span className="tb-key">DB</span><span className="tb-ok">D1·OK</span></span>
    </div>
  );
}

// ===== Topbar =====
function Topbar({ currentView, onNavigate, theme, onToggleTheme }: { currentView: string; onNavigate: (v: string) => void; theme: string; onToggleTheme: () => void }) {
  return (
    <header className="topbar">
      <div className="topbar-left">
        <div className="brand">
          <div className="brand-seal">
            <span className="seal-char">T</span>
          </div>
          <div className="brand-text">
            <div className="brand-name">TALENT <span className="brand-en">ATS</span><span className="blink-cursor" /></div>
            <div className="brand-tag">RECRUITMENT · FULL CYCLE</div>
          </div>
        </div>
      </div>
      <nav className="topbar-nav" aria-label="主导航">
        {Object.keys(TOP_NAV).map(view => (
          <button
            key={view}
            className={'topbar-nav-item' + (currentView === view ? ' active' : '')}
            onClick={() => onNavigate(view)}
          >
            {TOP_NAV_LABELS[view]}
          </button>
        ))}
      </nav>
      <div className="topbar-right">
        <LiveClock />
        <div className="search-box">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input type="text" placeholder="搜索候选人、岗位、流程…" aria-label="搜索" />
          <kbd>⌘K</kbd>
        </div>
        <button className="theme-toggle" onClick={onToggleTheme} title="切换主题" aria-label="切换主题">
          {theme === 'dark' ? (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
            </svg>
          ) : (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
            </svg>
          )}
        </button>
        <button className="icon-btn" title="通知" aria-label="通知">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          <span className="badge-dot" />
        </button>
        <div className="user-chip">
          <div className="avatar">林</div>
          <div className="user-info">
            <div className="user-name">林婉清</div>
            <div className="user-role">HR Director</div>
          </div>
        </div>
      </div>
    </header>
  );
}

// ===== Sidebar =====
function Sidebar({
  currentModule,
  openStages,
  toggleStage,
  onOpenModule,
}: {
  currentModule: string;
  openStages: Set<number>;
  toggleStage: (id: number) => void;
  onOpenModule: (m: string) => void;
}) {
  return (
    <aside className="sidebar">
      <div className="sidebar-header">
        <div className="sidebar-title">全流程导航</div>
        <div className="sidebar-sub">23 modules · 6 stages</div>
      </div>

      {STAGES.map(stage => (
        <div key={stage.id} className={'stage' + (openStages.has(stage.id) ? ' open' : '')} data-stage={stage.id}>
          <button className="stage-header" onClick={() => toggleStage(stage.id)} aria-expanded={openStages.has(stage.id)}>
            <span className="stage-num">{stage.num}</span>
            <span className="stage-name">{stage.name}</span>
            <span className="stage-arrow">▸</span>
          </button>
          <ul className="stage-modules">
            {stage.modules.map(m => (
              <li
                key={m.id}
                className={currentModule === m.id ? 'active' : ''}
                onClick={() => onOpenModule(m.id)}
              >
                <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M9 6l6 6-6 6" />
                </svg>
                {m.name}
              </li>
            ))}
          </ul>
        </div>
      ))}

      <div className="sidebar-footer">
        <div className="hint-card">
          <div className="hint-title">今日待办</div>
          <div className="hint-count">12</div>
          <div className="hint-text">面试 5 · 审批 3 · 跟进 4</div>
        </div>
      </div>
    </aside>
  );
}

// ===== Quick Panel =====
function QuickPanel() {
  const { data: activity } = useApi(() => api.activity(), []);
  const { data: funnel } = useApi(() => api.funnel(), []);

  return (
    <aside className="quickpanel">
      <div className="qp-section">
        <div className="qp-title">
          <span>实时流程</span>
          <span className="qp-pulse" />
        </div>
        <div className="qp-stream">
          {activity?.map((a) => (
            <div key={a.id} className="stream-item">
              <span className="time">{a.time}</span>
              <span className="text" dangerouslySetInnerHTML={{ __html: a.text }} />
            </div>
          ))}
        </div>
      </div>

      <div className="qp-section">
        <div className="qp-title">招聘漏斗 · 本月</div>
        <div className="funnel-mini">
          {funnel?.map(f => {
            const w = f.stage === '需求' || f.stage === '简历' ? 100 : Math.max(f.pct * 1.6, 6);
            return (
              <div key={f.id} className="f-row">
                <span className="f-label">{f.stage}</span>
                <div className="f-bar" style={{ ['--w' as any]: w + '%' }} />
                <span className="f-num">{f.count}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="qp-section">
        <div className="qp-title">
          <span className="prompt">SYS_LOG</span>
          <span className="qp-pulse" />
        </div>
        <div className="log-feed">
          <div className="log-line"><span className="log-ts">02:14:01</span> indexed 1284 resumes</div>
          <div className="log-line"><span className="log-ts">02:14:02</span> digest pipeline OK</div>
          <div className="log-line"><span className="log-ts">02:14:05</span> queued 247 → 5 lanes</div>
          <div className="log-line"><span className="log-ts">02:14:11</span> semantic index refresh</div>
          <div className="log-line"><span className="log-ts">02:14:13</span> 9 offers dispatched</div>
          <div className="log-line"><span className="log-ts">02:14:19</span> 3 waiting approval</div>
        </div>
      </div>

      <div className="qp-section">
        <div className="qp-title">日历</div>
        <div className="mini-cal">
          <div className="cal-week">
            <span>一</span><span>二</span><span>三</span><span>四</span><span>五</span><span className="we">六</span><span className="we">日</span>
          </div>
          <div className="cal-days">
            <span className="muted">29</span><span className="muted">30</span>
            <span>1</span><span>2</span><span>3</span><span className="we">4</span><span className="we">5</span>
            <span>6</span><span className="today">7</span><span>8</span><span>9</span><span>10</span><span className="we">11</span><span className="we">12</span>
            <span>13</span><span>14</span><span>15</span><span>16</span><span>17</span><span className="we">18</span><span className="we">19</span>
            <span>20</span><span>21</span><span>22</span><span>23</span><span>24</span><span className="we">25</span><span className="we">26</span>
          </div>
          <div className="cal-events">
            <div className="ev"><span className="ev-dot" style={{ background: 'var(--cinnabar)' }} />10:00 终面 · 赵睿</div>
            <div className="ev"><span className="ev-dot" style={{ background: 'var(--bamboo)' }} />14:30 用人评审</div>
            <div className="ev"><span className="ev-dot" style={{ background: 'var(--gold)' }} />16:00 Offer 审批</div>
          </div>
        </div>
      </div>
    </aside>
  );
}
